import { useState, useReducer, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  AccordionIcon,
  Flex,
  Box,
  useBreakpointValue,
  Button,
  Icon,
  Text,
  useBoolean,
  Image,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaArrowRight, FaDownload, FaClock, FaMagic, FaPlus, FaUpload } from "react-icons/fa";
import { ReactComponent as BrandTextIcon } from "../../../assets/logo/smp-logo-with-text.svg";
import useLocalStorage from "../../../_hooks/useLocalStorage";
import { isFeatureAvailable } from "../../../_utils/common";
import { FeatureCodes } from "../../details/constants";
import TldrUpgradeSubscriptionModal from "../../common/modals/TldrUpgradeSubscriptionModal";
import SnapshotMakerPreview from "./SnapshotMakerPreview";
import SnapshotMakerTemplateList from "./SnapshotMakerTemplateList";
import SnapshotMakerSizeSettings from "./SnapshotMakerSizeSettings";
import SnapshotMakerBackgroundSettings from "./SnapshotMakerBackgroundSettings";
import SnapshotMakerAppearanceSettings from "./SnapshotMakerAppearanceSettings";
import { STEPS, TEMPLATES, DEFAULT_TEMPLATE_DATA } from "./snapshotMaker.constants";
import { snapshotMakerReducer, snapshotMakerInit } from "./snapshotMaker.reducers";
import { LOGIN, PUBLISHING, SNAPSHOT_MAKER_GENERATE_PREVIEW } from "apps/simplified-web/src/_utils/routes";
import { SNAPSHOT_MAKER_GENERATE_IMAGE } from "apps/simplified-web/src/_actions/endpoints";
import { showToast } from "apps/simplified-web/src/_utils/showToast";

const boxStyles = {
  borderRadius: "md",
  background: "brand.dark_04",
  paddingY: "2",
  marginBottom: "3"
};

const SnapshotMaker = ({
  sourceTab,
  description,
  defaultTemplateData,
  saveData,
  onStateChange,
  onCustomizeClick,
  onUse,
}) => {
  const { t } = useTranslation(["snapshot-maker", "common", "accounts"]);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const history = useHistory();
  const [upgradeModal, setUpgradeModal] = useState();
  const [loading, setLoading] = useState(false);
  const [sourceAccordionOpen, setSourceAccordionOpen] = useBoolean();
  const [sizeAccordionOpen, setSizeAccordionOpen] = useBoolean();
  const [templateAccordionOpen, setTemplateAccordionOpen] = useBoolean();
  const [backgroundAccordionOpen, setBackgroundAccordionOpen] = useBoolean();
  const [appearanceAccordionOpen, setAppearanceAccordionOpen] = useBoolean();
  const [snapshotMakerSavedState, setSnapshotMakerSavedState] = useLocalStorage(saveData ? "snapshot-maker" : null);
  const [snapshotMakerState, dispatchState] = useReducer(snapshotMakerReducer, snapshotMakerSavedState, snapshotMakerInit);
  const { step, watermark, templateId, sizeConfig, backgroundConfig, cardConfig, templateData, result } = snapshotMakerState;
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const subscription = useSelector((state) => state.subscription);
  const canDisableWatermark = useMemo(
    () =>
      isFeatureAvailable(
        subscription,
        FeatureCodes.SNAPSHOT_MAKER_DISABLE_WATERMARK,
        false
      ),
    [subscription]
  );

  const handleTemplateChange = useCallback((template) => {
    if (loading) return;

    dispatchState({
      type: "setTemplate",
      payload: template,
    });
  }, [dispatchState, loading]);

  const handleSizeConfigChange = useCallback((config) => {
    if (loading) return;

    dispatchState({
      type: "setSize",
      payload: config,
    });
  }, [dispatchState, loading]);

  const handleBackgroundConfigChange = useCallback((config) => {
    if (loading) return;

    dispatchState({
      type: "setBackground",
      payload: config,
    });
  }, [dispatchState, loading]);

  const handleCardConfigChange = useCallback((name, value) => {
    if (loading) return;

    if (name) {
      dispatchState({
        type: "updateCard",
        payload: {
          [name]: value,
        },
      });
    }
  }, [dispatchState, loading]);

  const handleCustomize = useCallback(async () => {
    if (loading) return;

    if (onCustomizeClick) {
      setLoading(true);
  
      const result = await onCustomizeClick?.();
  
      setLoading(false);

      if (result) {
        dispatchState({
          type: "setStep",
          payload: STEPS.customize,
        });
      }
    } else {
      dispatchState({
        type: "setStep",
        payload: STEPS.customize,
      });
    }
  }, [onCustomizeClick, setLoading, dispatchState, loading]);

  const handleCustomizeAdvance = useCallback(() => {
    if (loading) return;

    if (isAuthenticated) {
      dispatchState({
        type: "setStep",
        payload: STEPS.customize_advance,
      });
    } else {
      history.push(LOGIN, { from: history.location });
    }
  }, [dispatchState, isAuthenticated, history, loading]);

  const handleNewImage = useCallback(() => {
    if (loading) return;

    dispatchState({
      type: "setStep",
      payload: isAuthenticated && !templateId ? STEPS.customize_advance : STEPS.customize,
    });
  }, [dispatchState, loading, isAuthenticated, templateId]);

  // @NOTE Currently not used
  // const handleStartOver = useCallback(() => {
  //   if (loading) return;

  //   dispatchState({
  //     type: "init",
  //   });
  // }, [dispatchState, loading]);

  const handleUse = useCallback(() => {
    onUse?.({
      url: result.url,
      mimeType: "image/png",
    });
  }, [onUse, result]);

  const handleSchedulePost = useCallback(() => {
    history.push(PUBLISHING, {
      openPostEditor: true,
      postData: {},
      addPostMedia: [
        {
          url: result.url,
          thumbUrl: result.url,
          mimeType: "image/png",
          external: true,
        },
      ],
    });
  }, [history, result]);

  const getImage = useCallback(async () => {
    try {
      const response = await fetch(SNAPSHOT_MAKER_GENERATE_IMAGE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: `${window.location.origin}${SNAPSHOT_MAKER_GENERATE_PREVIEW}`,
          pageSize: {
            width: snapshotMakerState.sizeConfig.width,
            height: snapshotMakerState.sizeConfig.height,
          },
          fileType: "png",
          pageData: {
            templateData: snapshotMakerState.templateData,
            sizeConfig: snapshotMakerState.sizeConfig,
            backgroundConfig: snapshotMakerState.backgroundConfig,
            cardConfig: snapshotMakerState.cardConfig,
            watermark: snapshotMakerState.watermark,
          },
        }),
      });
      const responseData = await response.json();

      if (responseData?.url) {
        return responseData;
      }

      return {
        error: response?.error?.message || response?.message || t("api_unknown_error", { ns: "common" }),
      };
    } catch (e) {
      return {
        error: e.message || t("api_unknown_error", { ns: "common" }),
      };
    }
  }, [snapshotMakerState, t]);

  const handleGenerate = useCallback(async () => {
    if (loading) return;

    setLoading(true);

    const data = await getImage();

    if (data?.url) {
      dispatchState({
        type: "setResult",
        payload: {
          url: data.url,
        },
      });
      dispatchState({
        type: "setStep",
        payload: STEPS.result,
      });
    } else {
      dispatchState({
        type: "setResult",
        payload: null,
      });

      showToast({
        description: data.error,
        title: "Error",
        status: "error",
      });
    }

    setLoading(false);
  }, [getImage, setLoading, dispatchState, loading]);

  const handleWatermarkChange = useCallback((value) => {
    if (loading) return;

    if (canDisableWatermark) {
      dispatchState({
        type: "setWatermark",
        payload: value,
      });
    } else {
      setUpgradeModal({
        title: t("upgrade_modal.features.snapshot_maker_disable_watermark.title", { ns: "accounts" }),
      });
    }
  }, [dispatchState, canDisableWatermark, setUpgradeModal, loading, t]);

  useEffect(() => {
    if (step === STEPS.start) {
      setSourceAccordionOpen.off();
      setSizeAccordionOpen.off();
      setTemplateAccordionOpen.off();
      setBackgroundAccordionOpen.off();
      setAppearanceAccordionOpen.off();
    } else if (step === STEPS.customize) {
      setTemplateAccordionOpen.on();
    } else if (step === STEPS.customize_advance) {
      setTemplateAccordionOpen.off();
      setBackgroundAccordionOpen.on();
    }
  }, [step]);

  useEffect(() => {
    if (!result && step === STEPS.result) {
      dispatchState({
        type: "setStep",
        payload: STEPS.start,
      });
    }
  }, [result]);

  useEffect(() => {
    if (loading) {
      setSourceAccordionOpen.off();
      setSizeAccordionOpen.off();
      setTemplateAccordionOpen.off();
      setBackgroundAccordionOpen.off();
      setAppearanceAccordionOpen.off();
    }
  }, [loading]);

  useEffect(() => {
    dispatchState({
      type: "setTemplateData",
      payload: defaultTemplateData,
    });
  }, [defaultTemplateData]);

  useEffect(() => {
    if (!canDisableWatermark) {
      dispatchState({
        type: "setWatermark",
        payload: true,
      });
    }
  }, [canDisableWatermark]);

  useEffect(() => {
    if (!isAuthenticated && step === STEPS.customize_advance) {
      dispatchState({
        type: "setStep",
        payload: STEPS.customize,
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setSnapshotMakerSavedState(snapshotMakerState);
    onStateChange?.(snapshotMakerState);
  }, [snapshotMakerState]);

  const renderHeader = () => (
    <Box marginBottom="5">
      <Icon as={BrandTextIcon} fontSize="2rem" width="auto" />
      {description && (
        <Text marginTop="4" fontSize="xl" padding="0">{description}</Text>
      )}
    </Box>
  );

  return (
    <>
      <Flex flexDirection={isSmallScreen ? "column" : "row"} color="white">
        <Box flex={isSmallScreen ? "1" : "1 1 53%"}>
          {isSmallScreen && renderHeader()}
          <Box
            position="sticky"
            top="var(--sticky-top-offset, 0px)"
            borderRadius="md"
            background="brand.dark_02"
            overflow="hidden"
            sx={{
              display: "flex",
              aspectRatio: "1", // @NOTE chakra does not support it as a parameter
            }}
          >
            {step === STEPS.result ? (
              <Image
                src={result.url}
                objectFit="contain"
                width="full"
                height="full"
                margin="auto"
              />
            ): (
              <Box
                flex="1"
                maxWidth={(sizeConfig && sizeConfig.height > sizeConfig.width) ? `${sizeConfig.width / sizeConfig.height * 100}%` : null}
                margin="auto"
              >
                <SnapshotMakerPreview
                  data={templateData}
                  sizeConfig={sizeConfig}
                  backgroundConfig={backgroundConfig}
                  cardConfig={cardConfig}
                  watermark={watermark}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          flex={isSmallScreen ? "1" : "1 1 47%"}
          marginTop={isSmallScreen ? "6" : null}
          marginLeft={isSmallScreen ? null : "8"}
        >
          {!isSmallScreen && renderHeader()}
          {[STEPS.start, STEPS.customize, STEPS.customize_advance].includes(step) && (
            <>
              {sourceTab && (
                <Accordion
                  allowToggle
                  index={(step === STEPS.start || sourceAccordionOpen) ? 0 : null}
                  onChange={(step !== STEPS.start && !loading) ? setSourceAccordionOpen.toggle : null}
                  sx={boxStyles}
                >
                  <AccordionItem border="0">
                    <AccordionButton cursor={step === STEPS.start ? "inherit !important" : null}>
                      <Box flex="1" textAlign="left" fontSize="lg">
                        {sourceTab.title}
                      </Box>
                      {step !== STEPS.start && <AccordionIcon />}
                    </AccordionButton>
                    <AccordionPanel paddingY="2">
                      {sourceTab.content}
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}
              <Accordion
                allowToggle
                index={(step === STEPS.start || sizeAccordionOpen) ? 0 : null}
                onChange={(step !== STEPS.start && !loading) ? setSizeAccordionOpen.toggle : null}
                sx={boxStyles}
              >
                <AccordionItem border="0">
                  <AccordionButton cursor={step === STEPS.start ? "inherit !important" : null}>
                    <Box flex="1" textAlign="left" fontSize="lg">
                      {t("tab.size")}
                    </Box>
                    {step !== STEPS.start && <AccordionIcon />}
                  </AccordionButton>
                  <AccordionPanel paddingY="2">
                    <SnapshotMakerSizeSettings
                      config={sizeConfig}
                      onConfigChange={handleSizeConfigChange}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </>
          )}
          {[STEPS.customize, STEPS.customize_advance].includes(step) && (
            <Accordion
              allowToggle
              index={templateAccordionOpen ? 0 : null}
              onChange={loading ? null : setTemplateAccordionOpen.toggle}
              sx={boxStyles}
            >
              <AccordionItem border="0">
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontSize="lg">
                    {t("tab.templates")}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel paddingY="2">
                  <Box maxHeight="320px" overflowY="auto" padding="2" margin="-2">
                    <SnapshotMakerTemplateList
                      selectedId={templateId}
                      items={TEMPLATES}
                      previewData={DEFAULT_TEMPLATE_DATA}
                      sizeConfig={sizeConfig}
                      watermark={!isAuthenticated}
                      onTemplateChange={handleTemplateChange}
                    />
                  </Box>
                  {step === STEPS.customize && (
                    <Box marginTop="4">
                      <Button
                        isFullWidth
                        leftIcon={<FaMagic />}
                        onClick={handleCustomizeAdvance}
                      >
                        {t("action.customize_design")}
                      </Button>
                    </Box>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
          {[STEPS.customize_advance].includes(step) && isAuthenticated && (
            <>
              <Accordion
                allowToggle
                index={backgroundAccordionOpen ? 0 : null}
                onChange={loading ? null : setBackgroundAccordionOpen.toggle}
                sx={boxStyles}
              >
                <AccordionItem border="0">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="lg">
                      {t("tab.background")}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <SnapshotMakerBackgroundSettings
                      config={backgroundConfig}
                      onConfigChange={handleBackgroundConfigChange}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              <Accordion
                allowToggle
                index={appearanceAccordionOpen ? 0 : null}
                onChange={loading ? null : setAppearanceAccordionOpen.toggle}
                sx={boxStyles}
              >
                <AccordionItem border="0">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="lg">
                      {t("tab.appearance")}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <SnapshotMakerAppearanceSettings
                      config={cardConfig}
                      watermark={watermark}
                      onConfigChange={handleCardConfigChange}
                      onWatermarkChange={handleWatermarkChange}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </>
          )}
          {step === STEPS.start && (
            <Box
              sx={{
                ...boxStyles,
                padding: "4",
              }}
            >
              <Button
                variant="solid"
                isFullWidth
                isLoading={loading}
                rightIcon={<FaArrowRight />}
                onClick={handleCustomize}
              >
                {t("next", { ns: "common" })}
              </Button>
            </Box>
          )}
          {[STEPS.customize, STEPS.customize_advance].includes(step) && (
            <Box
              sx={{
                ...boxStyles,
                padding: "4",
              }}
            >
              <Button
                variant="solid"
                isFullWidth
                isLoading={loading}
                rightIcon={<FaArrowRight />}
                onClick={handleGenerate}
              >
                {t("generate", { ns: "common" })}
              </Button>
            </Box>
          )}
          {step === STEPS.result && (
            <Wrap spacing="3">
              {onUse ? (
                <WrapItem>
                  <Button
                    size="sm"
                    variant="solid"
                    leftIcon={<FaUpload />}
                    onClick={handleUse}
                  >
                    {t("action.use_image")}
                  </Button>
                </WrapItem>
              ) : (
                <WrapItem>
                  <Button
                    size="sm"
                    variant="solid"
                    leftIcon={<FaClock />}
                    onClick={handleSchedulePost}
                  >
                    {t("action.schedule_post")}
                  </Button>
                </WrapItem>
              )}
              <WrapItem>
                <Button
                  as="a"
                  href={result.url}
                  download
                  size="sm"
                  leftIcon={<FaDownload />}
                >
                  {t("download", { ns: "common" })}
                </Button>
              </WrapItem>
              <WrapItem>
                <Button
                  leftIcon={<FaPlus />}
                  onClick={handleNewImage}
                  variant="ghost"
                  size="sm"
                >
                  {t("action.new_image")}
                </Button>
              </WrapItem>
            </Wrap>
          )}
        </Box>
      </Flex>
      {upgradeModal && (
        <TldrUpgradeSubscriptionModal
          show={true}
          onHide={() => setUpgradeModal(null)}
          title={upgradeModal.title}
        />
      )}
    </>
  );
};


SnapshotMaker.propTypes = {
  sourceTab: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.node,
  }),
  description: PropTypes.string,
  defaultTemplateData: PropTypes.shape({}),
  saveData: PropTypes.bool,
  onStateChange: PropTypes.func,
  onCustomizeClick: PropTypes.func,
  onUse: PropTypes.func,
};

SnapshotMaker.defaultProps = {
  sourceTab: undefined,
  description: undefined,
  defaultTemplateData: undefined,
  saveData: undefined,
  onStateChange: undefined,
  onCustomizeClick: undefined,
  onUse: undefined,
};

export default SnapshotMaker;
